/**
 * EOS Firestore Collection Types
 * 
 * These types model the Supabase schema as Firestore collections.
 * Each table becomes a Firestore collection with flat, normalized data structures.
 * Foreign key relationships are maintained using document IDs (strings).
 */

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// Core Entity Types
// ============================================================================

/**
 * Users Collection
 * Represents system users
 * 
 * Note: This interface includes both EOS-specific fields and legacy CRM fields
 * that exist in the Firestore user documents.
 */
export interface User {
  id: string;
  uid?: string; // Alternative ID field (often same as id)
  email: string;
  Email?: string; // Alternative email field (legacy)
  firstName?: string;
  lastName?: string;
  name: string; // Computed: firstName + ' ' + lastName or Name field
  Name?: string; // Alternative name field (legacy)
  title?: string;
  role: 'super_user' | 'user' | string; // EOS role, but can be any string from Firestore
  CRMRole?: string; // CRM role field (e.g., "CRM Admin")
  CRMUser?: boolean; // Whether user is a CRM user
  createdAt?: Timestamp;
  invitedAt?: Timestamp;
  invitationAcceptedAt?: Timestamp;
  authProvider?: string; // Default: 'email' (e.g., 'google')
  receiveNotifications?: boolean; // Default: true
  notificationScope?: 'all' | 'tagged'; // Default: 'all'
  canPresentMeetings?: boolean; // Default: false
  lastActive?: Timestamp; // Default: now()
  teamId?: string; // Default: '00000000-0000-0000-0000-000000000001'
  SuperAdmin?: boolean; // Legacy admin check field (exists in Firestore user documents)
  isEOSUser?: boolean; // EOS access permission field
  isAdmin?: boolean; // Admin flag
  
  // CRM/Legacy fields
  Branch?: string; // User's branch location (e.g., "Jacksonville, FL")
  LicensedStates?: string[]; // Array of licensed states (e.g., ["FL", "GA", "NY"])
  Roles?: string[]; // Array of role strings (e.g., ["IT Specialist"])
  MarketingAdmin?: boolean; // Marketing admin flag
  Level1?: boolean; // Level1 flag
  NMLS?: string; // NMLS number
  SwagBudget?: string; // Swag budget
  photo?: string; // Photo URL
  phoneNo?: string[]; // Array of phone numbers
  website?: string; // Website
  dateJoined?: Timestamp | Date | string; // Date joined
  
  // FCM (Firebase Cloud Messaging) tokens
  fcmTokens?: string[]; // Array of FCM tokens for push notifications
  
  // Google Calendar integration
  googleCalendarConnected?: boolean;
  googleCalendarSettings?: {
    twoWaySync?: boolean;
    [key: string]: any;
  };
  googleCalendarToken?: {
    connectedAt?: Timestamp | Date | string;
    refresh_token?: string;
    isAdmin?: boolean;
    lastTokenUpdate?: Timestamp | Date | string;
    tokenExpiry?: number;
    [key: string]: any;
  };
  
  // Dialpad integration
  dialpadCapable?: boolean;
  dialpadEnabled?: boolean;
  dialpadMappedAt?: Timestamp | Date | string;
  dialpadMappedBy?: string; // User ID who mapped the dialpad
  dialpadUserId?: string;
  dialpadUserInfo?: {
    display_name?: string;
    emails?: string[];
    id?: string;
    office_id?: string;
    phone_numbers?: string[];
    state?: string;
    [key: string]: any;
  };
  
  // CRM notification settings
  crmNotificationSettings?: {
    atRiskDeals?: boolean;
    dailyDigest?: boolean;
    dealTransfers?: boolean;
    newLeadAssignments?: boolean;
    taskReminders?: boolean;
    [key: string]: any;
  };
  
  // CRM table preferences
  crmTablePreferences?: {
    columnOrder?: string[];
    filters?: Record<string, any>;
    tags?: string[];
    savedAt?: Timestamp | Date | string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    viewMode?: string;
    visibleColumns?: string[];
    [key: string]: any;
  };
  
  // Custom tags
  customTags?: string[];
  
  // Access tokens (legacy, should be handled securely)
  accessToken?: string;
  
  // Additional fields that may exist in Firestore
  [key: string]: any;
}

/**
 * Rocks Collection
 * Represents quarterly rocks (goals/objectives)
 */
export interface Rock {
  id: string;
  ownerId: string; // Reference to User.id
  collaborators: string[]; // Array of User.id
  rock: string;
  measurable: string;
  status: 'on-track' | 'off-track' | 'at-risk'; // Default: 'on-track'
  progress: number; // 0-100, Default: 0
  weekNumber: number; // 1-13, Default: 1
  dueDate: Date | string;
  notes: string; // Default: ''
  isAchieved: boolean; // Default: false
  isDropped: boolean; // Default: false
  achievedAt?: Timestamp;
  droppedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  rockType: 'individual' | 'company'; // Default: 'individual'
  updatedByUserId?: string; // Reference to User.id - tracks who last updated the rock
  off_track_consecutive_count?: number; // Default: 0 - consecutive meetings off-track
  off_track_total_count?: number; // Default: 0 - total times off-track
  departmentId?: string; // Reference to Department.id
}

/**
 * Issues Collection
 * Represents issues/problems that need to be resolved
 */
export interface Issue {
  id: string;
  ownerId: string; // Reference to User.id
  description: string;
  status: 'open' | 'in-progress' | 'solved'; // Default: 'open'
  priority: 'low' | 'medium' | 'high'; // Default: 'medium'
  solvedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isRock: boolean; // Default: false
  isMetric: boolean; // Default: false
  relatedRockId?: string; // Reference to Rock.id
  relatedMetricId?: string; // Reference to Metric.id
  name: string;
  firstOpenedDate: Timestamp;
  createdInMeetingId?: Timestamp; // Reference to ActiveMeeting.id (timestamp)
  assignedToUserId?: string; // Reference to User.id
  resolvedByUserId?: string; // Reference to User.id
  resolutionNotes?: string;
  departmentId?: string; // Reference to Department.id
  isCompany?: boolean; // Default: false
}

/**
 * Metrics Collection
 * Represents measurable metrics/goals
 */
export type GoalOrientation = 'gte' | 'lte' | 'eq' | 'range';

/**
 * Metrics Collection
 * Represents measurable metrics/goals
 */
export interface Metric {
  id: string;
  ownerId?: string; // Reference to User.id
  
  // This field stores the ID from the metric_names collection
  metricNameId: string; // Reference to MetricName.id

  // This field is for UI display only and will be joined in the hook.
  // It is not stored directly in the 'metrics' document.
  metricName?: string;
  title?: string; // Clear, concise title (EOS Standard)
  
  measurable: string;
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  year: number; // 2020-2100
  deletedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  category?: 'Sales' | 'Marketing' | 'Operations' | 'Finance' | 'Technology' | 'Customer Service' | 'Leadership';
  collaboratorId?: string; // Reference to User.id
  timePeriodType: 'weekly' | 'monthly' | 'quarterly'; // Default: 'quarterly'
  quarterlyGoal: number; // >= 0, Default: 0
  weeklyGoal?: number; // New field for weekly target
  measurementType: 'currency' | 'count' | 'percentage'; // Default: 'count'
  unit?: string; // 'number', 'percent', '$', 'time', etc.
  relatedRockId?: string; // Reference to Rock.id
  createdBy?: string; // Reference to User.id
  
  // EOS Scorecard specific fields
  goalOrientation?: GoalOrientation;
  goalRangeMin?: number;
  goalRangeMax?: number;
  active?: boolean;
  
  // Ordering and Organization
  displayOrder?: number;
  departmentId?: string; // Reference to Department.id
}

/**
 * Unified Action Items Collection
 * Represents all action items - both general and rock-specific.
 * Rock-specific items have a rockId field; general items may have linkedRockId, linkedIssueId, or linkedMetricId.
 */
export interface ActionItem {
  id: string;
  // Core fields
  description: string; // Primary field (replaces both description and task)
  ownerId?: string; // Reference to User.id
  assignedToUserId?: string; // Reference to User.id
  dueDate: Date | string;
  complete: boolean; // Default: false
  completedDate?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  dropped: boolean; // Default: false
  droppedReason?: string;
  
  // Rock association (optional - if present, it's rock-specific)
  rockId?: string; // Reference to Rock.id (for rock-specific action items)
  
  // Link fields (for general action items linked to entities)
  linkedRockId?: string; // For general action items linked to rocks
  linkedIssueId?: string; // Reference to Issue.id
  linkedMetricId?: string; // Reference to Metric.id
  
  // Legacy/backward compatibility fields
  task?: string; // Deprecated - use description instead
  assigneeId?: string; // Deprecated - use assignedToUserId instead
  issueId?: string; // Deprecated - use linkedIssueId instead
  
  // Meeting/creation fields
  createdInMeetingId?: string; // Reference to ActiveMeeting.id
  createdByUserId: string; // Reference to User.id
  carriedFromActionItemId?: string; // Reference to ActionItem.id
}

// ============================================================================
// Meeting Types
// ============================================================================

/**
 * Active Meetings Collection
 * Represents currently active L10 meetings
 */
export interface ActiveMeeting {
  id: string;
  meetingTimestamp: Timestamp;
  meetingDate: Date | string;
  currentSectionIndex: number; // Default: 0
  sectionStartedAt: Timestamp;
  totalStartedAt: Timestamp;
  presenterUserId: string; // Reference to User.id
  selectedUserId: string; // Default: 'all'
  rockTypeFilter: 'all' | 'company' | 'individual'; // Default: 'all'
  idsFilterTab: string; // Default: 'all'
  isActive: boolean; // Default: true
  segueAutoPopulated?: boolean; // Default: false
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * L10 Notes Collection
 * Represents notes taken during L10 meetings
 */
export interface L10Note {
  id: string;
  meetingTimestamp: Timestamp;
  section: string;
  notes: string;
  createdAt: Timestamp;
  meetingDate: Date | string;
  updatedAt: Timestamp;
  meetingSection?: string;
  assignedDepartments?: any[]; // JSONB array, Default: []
  assignedUsers?: any[]; // JSONB array, Default: []
  noteCategory?: 'general' | 'department_communication'; // Default: 'general'
}

/**
 * Meeting Issues Collection
 * Represents issues discussed in meetings
 */
export interface MeetingIssue {
  id: string;
  meetingTimestamp: Timestamp;
  issueId: string; // Reference to Issue.id
  addedAutomatically: boolean; // Default: false
  addedByUserId: string; // Reference to User.id
  resolvedInThisMeeting: boolean; // Default: false
  resolutionNotes?: string;
  resolvedAt?: Timestamp;
  weeksUnresolvedAtAdd: number; // Default: 0
  createdAt: Timestamp;
}

/**
 * Meeting Off Track Metrics Collection
 * Represents metrics that were off-track in a meeting
 */
export interface MeetingOffTrackMetric {
  id: string;
  meetingTimestamp: Timestamp;
  metricId: string; // Reference to Metric.id
  createdAt: Timestamp;
}

/**
 * Meeting Rock Discussions Collection
 * Represents rock discussions in meetings
 */
export interface MeetingRockDiscussion {
  id: string;
  meetingId: Timestamp; // Reference to ActiveMeeting.meetingTimestamp
  rockId: string; // Reference to Rock.id
  flaggedForDiscussion: boolean; // Default: false
  reviewed: boolean; // Default: false
  flaggedAt?: Timestamp;
  reviewedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Wins Excited Collection
 * Represents wins and things people are excited about
 */
export interface WinExcited {
  id: string;
  meetingTimestamp?: Timestamp;
  userId: string; // Reference to User.id
  type: 'win' | 'excited';
  content: string;
  createdAt: Timestamp;
  taggedUserId?: string; // Reference to User.id
}

// ============================================================================
// Rock-Related Types
// ============================================================================


/**
 * Milestones Collection
 * Represents milestones for rocks
 */
export interface Milestone {
  id: string;
  rockId: string; // Reference to Rock.id
  task: string;
  complete: boolean; // Default: false
  dueDate: Date | string;
  sortOrder: number; // Default: 0
  createdAt: Timestamp;
  completedAt?: Timestamp;
}

/**
 * Rock Notes Collection
 * Represents notes on rocks
 */
export interface RockNote {
  id: string;
  rockId: string; // Reference to Rock.id
  note: string;
  createdById: string; // Reference to User.id
  createdAt: Timestamp;
}

/**
 * Rock Triage Notes Collection
 * Represents triage notes for rocks (one per rock)
 */
export interface RockTriageNote {
  id: string;
  rockId: string; // Reference to Rock.id (UNIQUE)
  whyNotes: string; // Default: ''
  whatNotes: string; // Default: ''
  takeawaysNotes: string; // Default: ''
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Rock Evaluations Collection
 * Represents AI evaluations of rocks
 */
export interface RockEvaluation {
  id: string;
  rockId: string; // Reference to Rock.id
  overallScore: number; // 0-100
  smartScore: number; // 0-100
  clarityScore: number; // 0-100
  scopeScore: number; // 0-100
  impactScore: number; // 0-100
  actionScore: number; // 0-100
  strengths: any[]; // JSONB array, Default: []
  weaknesses: any[]; // JSONB array, Default: []
  recommendations: any[]; // JSONB array, Default: []
  aiRewriteRock?: string;
  aiRewriteMeasurable?: string;
  aiExplanation?: string;
  evaluatedAt: Timestamp;
  evaluatedBy: string; // Reference to User.id
  rockSnapshot: Record<string, any>; // JSONB object, Default: {}
  createdAt: Timestamp;
  suggestionAcknowledgedAt?: Timestamp; // When user viewed/acknowledged the AI suggestions
  acknowledgedBy?: string; // Reference to User.id who acknowledged
}

// ============================================================================
// Metric-Related Types
// ============================================================================

/**
 * Metric Names Collection
 * Represents available metric names
 */
export interface MetricName {
  id: string;
  name: string; // UNIQUE
  createdAt: Timestamp;
  createdBy?: string; // Reference to auth.users.id (Auth user ID)
}

/**
 * Metric Goals Collection
 * Represents goals for metrics
 */
export interface MetricGoal {
  id: string;
  metricId: string; // Reference to Metric.id
  periodType: 'week' | 'month' | 'quarter';
  periodNumber: number;
  goalValue?: number; // >= 0
  actualValue?: number;
  status: 'on-track' | 'at-risk' | 'off-track' | 'no-data'; // Calculated based on actualValue vs goalValue
  createdAt: Timestamp;
  updatedAt: Timestamp;
  dateStart: Date | string; // Default: CURRENT_DATE
  dateEnd: Date | string; // Default: CURRENT_DATE
  updatedByUserId?: string; // Reference to User.id - tracks who last updated the metric goal
  
  // New fields
  notes?: string;
  isCustomGoal?: boolean;
}

/**
 * Metric Actuals History Collection
 * Represents historical actual values for metrics
 */
export interface MetricActualsHistory {
  id: string;
  metricGoalId?: string; // Reference to MetricGoal.id
  metricId: string; // Reference to Metric.id
  actualValue: number;
  periodType: 'week' | 'month' | 'quarter';
  periodNumber: number; // 1-52
  recordedAt: Timestamp;
  createdAt: Timestamp;
}

/**
 * Scorecard Trending Collection
 * Represents trending data for scorecard metrics
 */
export interface ScorecardTrending {
  id: string;
  metricId: string; // Reference to Metric.id
  weekNumber: number;
  actual?: number;
  createdAt: Timestamp;
}

// ============================================================================
// AI-Related Types
// ============================================================================

/**
 * AI Conversations Collection
 * Represents AI chat conversations
 */
export interface AIConversation {
  id: string;
  rockId: string; // Reference to Rock.id
  userId: string; // Reference to User.id
  title?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * AI Conversation Messages Collection
 * Represents messages in AI conversations
 */
export interface AIConversationMessage {
  id: string;
  conversationId: string; // Reference to AIConversation.id
  role: 'user' | 'assistant' | 'system';
  content: string;
  tokens?: number;
  createdAt: Timestamp;
}

/**
 * AI Interaction Audit Collection
 * Represents audit logs for AI interactions
 */
export interface AIInteractionAudit {
  id: string;
  userId: string; // Reference to User.id
  rockId?: string; // Reference to Rock.id
  interactionType: 'evaluation' | 'chat_message' | 'suggestion';
  modelUsed: string;
  promptText?: string;
  promptTokens: number;
  responseText?: string;
  responseTokens: number;
  estimatedCostUsd: number;
  containedPii: boolean; // Default: false
  piiTypes?: string[]; // ARRAY
  injectionAttempt: boolean; // Default: false
  injectionPatterns?: string[]; // ARRAY
  rateLimitExceeded: boolean; // Default: false
  responseTimeMs?: number;
  ipAddress?: string; // inet -> string
  userAgent?: string;
  createdAt: Timestamp;
}

/**
 * AI Rate Limits Collection
 * Represents rate limits for AI usage
 */
export interface AIRateLimit {
  id: string;
  userId: string; // Reference to User.id
  limitType: 'daily_evaluations' | 'daily_chat_messages' | 'monthly_cost';
  currentCount: number; // Default: 0
  currentCostUsd: number; // Default: 0
  maxCount?: number;
  maxCostUsd?: number;
  periodStart: Timestamp;
  periodEnd: Timestamp;
  updatedAt: Timestamp;
}

/**
 * AI Terms Acceptance Collection
 * Represents user acceptance of AI terms
 */
export interface AITermsAcceptance {
  id: string;
  userId: string; // Reference to User.id
  termsVersion: string;
  acceptedAt: Timestamp;
  ipAddress?: string; // inet -> string
  userAgent?: string;
}

// ============================================================================
// Notification Types
// ============================================================================

/**
 * Notification Type Enum
 * Represents the different types of EOS notifications
 */
export type NotificationType = 
  | 'rock_created' | 'rock_completed' | 'rock_dropped' 
  | 'issue_created' | 'issue_resolved' 
  | 'action_item_created' | 'action_item_completed' 
  | 'metric_created' | 'wins_excited_created';

/**
 * Notifications Collection
 * Represents user notifications
 * 
 * Note: Notifications are stored in the user subcollection: users/{userId}/Notifications/{notificationId}
 * This structure matches the legacy notification system used throughout the app.
 * 
 * For EOS notifications, the following fields are used:
 * - user_id: The user who receives the notification (parent document ID in subcollection structure)
 * - triggeredByUserId: The user who caused the event (for filtering)
 */
export interface Notification {
  id: string;
  // Legacy fields (used by existing UI components)
  notification?: string; // Title/notification text (used by Navbar.jsx, ReadNotificationDrawer.jsx)
  message: string; // Message body
  timestamp: Timestamp; // When the notification was created (used by UI queries)
  read: boolean | Timestamp; // Read status - boolean for unread, Timestamp when read (used by UI queries)
  user?: { // User who triggered the notification (used by UI for avatar/display)
    uid: string;
    displayName: string;
    email: string;
    photoURL: string;
  };
  path?: string; // Route path for navigation (e.g., "/eos/rocks/{rockId}")
  route?: string; // Alternative route field
  loanID?: string; // For loan-related notifications (legacy)
  messageID?: string; // For message-related notifications (legacy)
  
  // EOS-specific fields (for EOS notifications)
  type?: string; // Notification type (e.g., 'rock_created', 'issue_resolved')
  title?: string; // Alternative title field (maps to 'notification' for display)
  relatedId?: string; // Related entity ID (e.g., rockId, issueId, actionItemId)
  triggeredByUserId?: string; // Reference to User.id who triggered the notification
  createdAt?: Timestamp; // Alternative timestamp field (maps to 'timestamp' for queries)
  isRead?: boolean; // Alternative read field (maps to 'read' for queries)
}

/**
 * Notification Preferences Collection
 * Represents user notification preferences
 */
export interface NotificationPreference {
  id: string;
  userId: string; // Reference to User.id
  preferenceType: 'rock_created' | 'rock_updated' | 'rock_completed' | 'rock_dropped' | 'issue_created' | 'issue_resolved' | 'action_item_created' | 'action_item_completed' | 'metric_created' | 'metric_off_track' | 'wins_excited_created';
  enabled: boolean; // Default: false
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Notification Recipients Collection
 * Represents users who should receive notifications
 */
export interface NotificationRecipient {
  id: string;
  userId: string; // Reference to User.id (UNIQUE)
  createdAt: Timestamp;
}

// ============================================================================
// Activity Logging Types
// ============================================================================

/**
 * User Activity Logs Collection
 * Represents user activity audit logs
 */
export interface UserActivityLog {
  id: string;
  userId: string; // Reference to User.id
  activityType: string;
  activityDescription: string;
  relatedEntityType?: string;
  relatedEntityId?: string; // Generic reference
  metadata: Record<string, any>; // JSONB object, Default: {}
  createdAt: Timestamp;
}

/**
 * Tangent Alerts Collection
 * Represents tangent alerts triggered during L10 meetings
 */
export interface TangentAlert {
  id: string;
  meetingId: string; // Reference to ActiveMeeting.id
  meetingTimestamp: Timestamp; // Reference to ActiveMeeting.meetingTimestamp
  triggeredByUserId: string; // Reference to User.id
  createdAt: Timestamp;
}

// ============================================================================
// VTO (Vision/Traction Organizer) Types
// ============================================================================

/**
 * V/TO Quarter Type
 */
export type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';

/**
 * A single Target Market / "The List" segment with EOS segmentation details.
 */
export interface TargetMarketEntry {
  name: string;
  demographic: string;
  geographic: string;
  psychographic: string;
}

/**
 * Container for the multi-entry Target Market list.
 */
export interface TargetMarketDetails {
  entries: TargetMarketEntry[];
}

/**
 * VTODocument (Vision/Traction Organizer)
 * Represents a single V/TO document for a specific quarter and year.
 * Stored in the 'vto' collection.
 *
 * NOTE: All properties are camelCase for consistency with Firestore best practices.
 */
export interface VTODocument {
  id: string;
  organizationName: string; // e.g., "Future Home Loans"
  quarter: Quarter;
  year: number;

  // --- Vision ---
  coreValues: string[]; // List of core values
  purpose: string; // Purpose/Cause/Passion
  niche: string; // Company's niche

  tenYearTarget: string; // The long-term, audacious goal
  
  // Marketing Strategy
  targetMarket: string; // "The List" - auto-populated with comma-separated entry names (back-compat / PDF)
  targetMarketDetails: TargetMarketDetails | null; // Structured multi-entry list with segmentation
  threeUniques: string[]; // Array of 3 strings
  provenProcess: string; // Visualized process (often stored as a URL or markdown)
  guarantee: string; // The company's guarantee

  // 3-Year Picture
  threeYearDate: string; // e.g., "December 31, 2027"
  threeYearRevenueTarget: number; // 3-Year revenue goal in raw dollars
  threeYearProfitTarget: number; // 3-Year profit goal in raw dollars
  threeYearForecastedLoans: number; // Expected funded loans for 3-Year review conversion
  threeYearReviewTargetPct: number; // Target review conversion % for 3-Year (e.g., 40 = 40%)
  threeYearMeasurables: string[]; // List of key metrics
  threeYearDescription: string[]; // List of descriptive statements

  // Actuals (progress tracking)
  currentRevenue: number; // Actual current revenue in raw dollars
  currentProfit: number; // Actual current profit in raw dollars
  currentFundedLoans: number; // Actual funded loan count
  currentReviews: number; // Actual 5-star Google review count

  // --- Traction ---
  // 1-Year Plan
  oneYearDate: string; // e.g., "December 31, 2025"
  oneYearRevenueTarget: number; // 1-Year revenue goal in raw dollars
  oneYearProfitTarget: number; // 1-Year profit goal in raw dollars
  oneYearForecastedLoans: number; // Expected funded loans for 1-Year review conversion
  oneYearReviewTargetPct: number; // Target review conversion % for 1-Year
  oneYearMeasurables: string[];
  oneYearGoals: string[]; // List of 3-7 annual goals

  // Rocks (Quarterly)
  rocksDate: string; // e.g., "March 31, 2025"
  rocksRevenueTarget: number; // Rocks-quarter revenue goal in raw dollars
  rocksProfitTarget: number; // Rocks-quarter profit goal in raw dollars
  rocksMeasurables: string[];
  rocksList: string[]; // List of 3-7 company rocks for the quarter

  // Issues
  issuesList: string[]; // Long-term issues list

  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy?: string; // Reference to User.id
  lastEditedBy?: string; // Reference to User.id
  departmentId?: string | null; // Reference to Department.id

  /**
   * @deprecated Free-text financial fields replaced by the numeric *Target fields above.
   * Retained as optional for backward compatibility with old documents; no longer read or written.
   */
  threeYearRevenue?: string;
  /** @deprecated see threeYearProfitTarget */
  threeYearProfit?: string;
  /** @deprecated see oneYearRevenueTarget */
  oneYearRevenue?: string;
  /** @deprecated see oneYearProfitTarget */
  oneYearProfit?: string;
  /** @deprecated see rocksRevenueTarget */
  rocksRevenue?: string;
  /** @deprecated see rocksProfitTarget */
  rocksProfit?: string;
  /** @deprecated actuals no longer track an updated-at timestamp */
  currentRevenueUpdatedAt?: Timestamp;
}

// ============================================================================
// Accountability Chart Types
// ============================================================================

/**
 * Role Collection
 * Represents a single role in the Accountability Chart.
 * Stored in the 'eos_accountability_roles' collection.
 */
export interface Role {
  id: string;
  title: string;
  ownerId?: string; // Reference to User.id
  ownerName?: string; // Denormalized for quick display
  subtitle?: string; // e.g., "LMA", "CEO"
  responsibilities: string[]; // List of key responsibilities
  parentId: string | null; // ID of the parent role, or null for root
  level: number; // 0 for root, 1 for children, etc.
  position: number; // Order within a level (Default: 0)
  sortOrder: number; // Order within a level (alias for position)
  createdBy?: string; // Reference to User.id
  lastEditedBy?: string; // Reference to User.id
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * RoleNode
 * Represents a Role node in the rendered tree structure.
 */
export interface RoleNode extends Role {
  children: RoleNode[];
}

// ============================================================================
// Department Types
// ============================================================================

/**
 * Department Collection
 * Represents organizational departments
 */
export interface Department {
  id: string;
  name: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  createdBy?: string; // User ID who created the department
  deletedAt?: Timestamp; // For soft delete - when set, department is deleted
}

// ============================================================================
// Ideas Types
// ============================================================================

/**
 * Ideas Collection
 * Represents improvement ideas and fixes
 */
export interface Idea {
  id: string;
  title: string;
  description: string;
  type: 'idea' | 'fix';
  status: 'new' | 'completed';
  createdBy: string; // Reference to User.id
  completedBy?: string; // Reference to User.id
  createdAt: Timestamp;
  completedAt?: Timestamp;
}

// ============================================================================
// Collection Name Constants
// ============================================================================

/**
 * Unified meeting interface combining meeting data with summary
 */
export interface UnifiedMeeting {
  meetingTimestamp: Timestamp;
  meetingDate: Date | string;
  summary?: MeetingSummary;
  hasSummary: boolean;
}

/**
 * Meeting Summaries Collection
 * Represents AI-generated summaries of L10 meetings
 */
export interface MeetingSummary {
  id: string;
  meetingTimestamp: Timestamp;
  meetingDate: Date | string;
  summary: string;
  issuesDiscussed?: string[];
  issuesResolved?: Array<{ id: string; name: string; resolutionNotes?: string } | string>; // Support both object and string formats for backward compatibility
  rocksAchieved?: Array<{ id: string; name: string; owner?: string }>;
  actionItemsCreated?: Array<{ id: string; title: string; name?: string }>;
  sectionNotes?: Record<string, string>;
  // AI Rating
  aiRating?: number; // 0-100 (overall)
  aiRatingDetails?: {
    overall_productivity: number; // 1-10
    meeting_focus: number; // 1-10
    issue_resolution_quality: number; // 1-10
    action_item_clarity: number; // 1-10
    confidence: number; // 0.00-1.00
  };
  strengths?: string[];
  improvements?: string[];
  recommendations?: string[];
  // Section Notes Structure
  presenterNotes?: Array<{ content: string }>;
  segueNotes?: {
    general?: Array<{ content: string }>;
    department_communications?: Array<{
      content: string;
      departments?: string[];
      assigned_users?: string[];
    }>;
  };
  scorecardNotes?: {
    general?: Array<{ content: string }>;
    department_communications?: Array<{
      content: string;
      departments?: string[];
      assigned_users?: string[];
    }>;
  };
  rockReviewNotes?: {
    general?: Array<{ content: string }>;
    department_communications?: Array<{
      content: string;
      departments?: string[];
      assigned_users?: string[];
    }>;
  };
  customerEmployeeHeadlines?: {
    general?: Array<{ content: string }>;
    department_communications?: Array<{
      content: string;
      departments?: string[];
      assigned_users?: string[];
    }>;
  };
  idsNotes?: {
    general?: Array<{ content: string }>;
    department_communications?: Array<{
      content: string;
      departments?: string[];
      assigned_users?: string[];
    }>;
  };
  concludeNotes?: {
    general?: Array<{ content: string }>;
    department_communications?: Array<{
      content: string;
      departments?: string[];
      assigned_users?: string[];
    }>;
  };
  // Generation status tracking
  status?: 'pending' | 'generating' | 'completed' | 'failed';
  requestedAt?: Timestamp;
  requestedBy?: string; // Reference to User.id who requested the summary
  errorMessage?: string; // Error message if status is 'failed'
  // Legacy fields for backward compatibility
  total_issues_discussed?: number;
  issues_resolved_count?: number;
  avg_meeting_rating?: number;
  createdAt: Timestamp;
  createdBy?: string; // Reference to User.id
}

/**
 * Conversations Collection
 * Represents chat conversations (direct messages or group chats)
 */
export interface Conversation {
  id: string;
  name?: string; // For group chats
  type: 'direct' | 'group';
  isPrivate: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastMessageAt?: Timestamp;
  members: string[]; // User IDs
}

/**
 * Conversation Members Subcollection
 * Tracks user membership in conversations with read receipts
 */
export interface ConversationMember {
  id: string;
  conversationId: string;
  userId: string;
  joinedAt: Timestamp;
  lastReadAt?: Timestamp;
  leftAt?: Timestamp;
}

/**
 * Chat Messages Subcollection
 * Individual messages within a conversation
 */
export interface ChatMessage {
  id: string;
  conversationId: string;
  userId: string;
  content: string;
  parentMessageId?: string;
  mentionedUserIds: string[];
  mentioned?: string[]; // Legacy format for compatibility with GroupChat
  mentionedObj?: Array<{
    Name: string;
    Email: string;
    uid: string;
    photoURL: string;
    isMention: boolean;
  }>;
  editedAt?: Timestamp;
  edited?: boolean; // Legacy format for compatibility with Message component
  deletedAt?: Timestamp;
  createdAt: Timestamp;
  timestamp?: Timestamp; // Legacy format for compatibility with Message component
  files?: Array<{ url: string; name: string }>;
  Files?: Array<{ url: string; name: string }>; // Legacy format
  user: {
    uid: string;
    displayName: string;
    photoURL?: string;
    email: string;
  };
  message?: string; // Legacy format - same as content
  parentID?: string; // Legacy format - same as conversationId
}

/**
 * Synced Calendar Events Collection
 * Tracks items synced to Google Calendar
 */
export interface SyncedCalendarEvent {
  id: string;
  userId: string;
  itemType: 'milestone' | 'action_item' | 'meeting';
  itemId: string;
  meetingId?: string; // For meeting events
  googleEventId: string;
  lastSyncedAt: Timestamp;
  syncedAt: Timestamp;
}

/**
 * Calendar Sync History Collection
 * Tracks sync operations and their results
 */
export interface CalendarSyncHistory {
  id: string;
  userId: string;
  syncType: 'manual' | 'automatic';
  calendarsSynced: string[];
  eventsCreated: number;
  eventsUpdated: number;
  eventsDeleted: number;
  status: 'success' | 'error' | 'partial';
  error?: string;
  syncedAt: Timestamp;
}

/**
 * Policy Collection
 * Represents company policies and documents
 */
export interface Policy {
  id: string;
  title: string;
  description?: string;
  category: string;
  content: string; // Policy content (markdown or HTML)
  fileUrl?: string; // If stored in Firebase Storage
  version: number;
  currentVersion: boolean;
  createdBy: string; // Reference to User.id
  createdAt: Timestamp;
  updatedAt: Timestamp;
  updatedBy?: string; // Reference to User.id
  tags?: string[];
}

export const COLLECTION_NAMES = {
  USERS: 'users',
  ROCKS: 'rocks',
  ISSUES: 'issues',
  METRICS: 'metrics',
  ACTIVE_MEETINGS: 'active_meetings',
  L10_NOTES: 'l10_notes',
  MEETING_ISSUES: 'meeting_issues',
  MEETING_OFF_TRACK_METRICS: 'meeting_off_track_metrics',
  MEETING_ROCK_DISCUSSIONS: 'meeting_rock_discussions',
  MEETING_SUMMARIES: 'meeting_summaries',
  WINS_EXCITED: 'wins_excited',
  ACTION_ITEMS: 'action_items', // Unified database collection name for all action items (both general and rock-specific)
  MILESTONES: 'milestones',
  ROCK_NOTES: 'rock_notes',
  ROCK_TRIAGE_NOTES: 'rock_triage_notes',
  ROCK_EVALUATIONS: 'rock_evaluations',
  METRIC_NAMES: 'metric_names',
  METRIC_GOALS: 'metric_goals',
  METRIC_ACTUALS_HISTORY: 'metric_actuals_history',
  SCORECARD_TRENDING: 'scorecard_trending',
  AI_CONVERSATIONS: 'ai_conversations',
  AI_CONVERSATION_MESSAGES: 'ai_conversation_messages',
  AI_INTERACTION_AUDIT: 'ai_interaction_audit',
  AI_RATE_LIMITS: 'ai_rate_limits',
  AI_TERMS_ACCEPTANCE: 'ai_terms_acceptance',
  NOTIFICATIONS: 'notifications',
  NOTIFICATION_PREFERENCES: 'notification_preferences',
  NOTIFICATION_RECIPIENTS: 'notification_recipients',
  USER_ACTIVITY_LOGS: 'user_activity_logs',
  TANGENT_ALERTS: 'tangent_alerts',
  VTO: 'vto', // Collection name in Firestore remains lowercase
  ACCOUNTABILITY_ROLES: 'eos_accountability_roles',
  DEPARTMENTS: 'departments',
  IDEAS: 'ideas',
  CONVERSATIONS: 'conversations',
  CONVERSATION_MEMBERS: 'conversation_members',
  CHAT_MESSAGES: 'chat_messages',
  SYNCED_CALENDAR_EVENTS: 'synced_calendar_events',
  CALENDAR_SYNC_HISTORY: 'calendar_sync_history',
  POLICIES: 'policies',
} as const;

